import { Paper, Skeleton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { IClientDetailsTableProperties } from '@bvi/admin-panel/feature/clients/ui/client-details-table/types';
import { formatCompanySizeLabel } from '@bvi/api-interfaces/company-size';
import { formatDate } from '@bvi/date-utils';

import { styles } from './styles';

const Cell: FC<{ label: string; value?: ReactNode; loading: boolean }> = ({
  label,
  value,
  loading,
}) => (
  <>
    <Grid xs={2} style={styles.head}>
      {label}
    </Grid>
    <Grid xs={4} style={styles.body}>
      {loading ? <Skeleton width="80%" /> : value ?? '—'}
    </Grid>
  </>
);

export const ClientDetailsTable: FC<IClientDetailsTableProperties> = ({
  client,
}) => {
  const { t } = useTranslation();
  const loading = !client;

  return (
    <Grid container component={Paper} rowSpacing={1} p={2}>
      <Cell
        label={t('clients.details.table.columns.companySize')}
        value={
          client ? formatCompanySizeLabel(client.company.size.name) : undefined
        }
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.emailDomain')}
        value={client?.company.emailDomain}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.subscriptionStartsAt')}
        value={
          client?.subscription.startsAt
            ? formatDate(client.subscription.startsAt)
            : undefined
        }
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.subscriptionExpiresAt')}
        value={
          client?.subscription.expiresAt
            ? formatDate(client.subscription.expiresAt)
            : undefined
        }
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.subscriptionTermMonths')}
        value={client?.subscription.termMonths}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.orientationAt')}
        value={
          client?.orientationAt ? formatDate(client.orientationAt) : undefined
        }
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.accountStatus')}
        value={
          client
            ? t(`clients.accountStatuses.${client.accountStatus}`)
            : undefined
        }
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.paymentMethod')}
        value={
          client?.paymentMethod
            ? t(`clients.paymentMethods.${client.paymentMethod}`)
            : undefined
        }
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.paymentMethodNotes')}
        value={client?.paymentMethodNotes}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.numberOfUsers')}
        value={client?.company.users.length}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.signatoryName')}
        value={client?.company.signatoryName}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.signatoryEmail')}
        value={client?.company.signatoryEmail}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.billingContactName')}
        value={client?.company.billingContactName}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.billingContactEmail')}
        value={client?.company.billingContactEmail}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.accountManagerName')}
        value={client?.accountManagerName}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.accountManagerEmail')}
        value={client?.accountManagerEmail}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.supportLiaisonName')}
        value={client?.supportLiaisonName}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.supportLiaisonEmail')}
        value={client?.supportLiaisonEmail}
        loading={loading}
      />
      <Cell
        label={t('clients.details.table.columns.status')}
        value={client?.status}
        loading={loading}
      />
      <Grid xs={2} style={styles.head}>
        {t('clients.details.table.columns.pricingNarrative')}
      </Grid>
      <Grid xs={10} style={styles.body}>
        {client ? client.pricingNarrative ?? '—' : <Skeleton width="80%" />}
      </Grid>
    </Grid>
  );
};
